/**
 * InvoicesModule Component
 * Lists and manages invoices in the Sales module
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import PdfViewerModal from "../shared/PdfViewerModal";
import { openAuthenticatedPdf } from '../../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// HtmlPreviewModal component
const HtmlPreviewModal = ({ title, url, onClose, testId }) => {
  if (!url) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4" data-testid={testId || 'html-preview-modal'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[92vh] sm:h-[86vh] flex flex-col">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800 truncate">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1">✕</button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto">
          <iframe src={url} title={title} className="w-full h-full border-0" style={{ minHeight: '100%' }} key={url} />
        </div>
      </div>
    </div>
  );
};

const InvoicesModule = ({ onBack, onEdit, onCreate }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('UNPAID');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [pdfView, setPdfView] = useState(null); // { type: 'invoice'|'receipt', id, title }
  const [previewPickerId, setPreviewPickerId] = useState(null); // invoice.id whose picker is open
  const [showEstimatePicker, setShowEstimatePicker] = useState(false);
  const [estimateOptions, setEstimateOptions] = useState([]);
  const [estimateSearch, setEstimateSearch] = useState('');
  const [showQuickPayment, setShowQuickPayment] = useState(false);
  const [quickPaymentInvoice, setQuickPaymentInvoice] = useState(null);
  const [quickPaymentForm, setQuickPaymentForm] = useState({
    amount: '',
    method: 'Manual',
    paid_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [quickPaymentSaving, setQuickPaymentSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales/invoices`);
      setInvoices(response.data);
      setLoadError('');
    } catch (error) {
      if (error.response?.status === 401) {
        return;
      }
      console.error('Error fetching invoices:', error);
      setLoadError('Unable to refresh invoices. Please reload.');
    } finally {
      setLoading(false);
    }
  };

  // Close the preview picker when clicking outside
  useEffect(() => {
    if (!previewPickerId) return;
    const handler = () => setPreviewPickerId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [previewPickerId]);

  const openQuickPayment = (invoice) => {
    const balance = invoice?.balance_due !== undefined
      ? Number(invoice.balance_due)
      : (Number(invoice.total || 0) - Number(invoice.amount_paid || 0));
    setQuickPaymentInvoice(invoice);
    setQuickPaymentForm({
      amount: balance > 0 ? balance.toFixed(2) : '',
      method: 'Manual',
      paid_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowQuickPayment(true);
  };

  const handleQuickPaymentSave = async () => {
    if (!quickPaymentInvoice) return;
    const amountValue = parseFloat(quickPaymentForm.amount);
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      alert('Please enter a valid payment amount.');
      return;
    }
    try {
      setQuickPaymentSaving(true);
      await axios.post(`${API}/sales/invoices/${quickPaymentInvoice.id}/payments`, {
        amount: amountValue,
        method: quickPaymentForm.method || 'Manual',
        paid_date: quickPaymentForm.paid_date || new Date().toISOString().split('T')[0],
        notes: quickPaymentForm.notes || ''
      });
      setShowQuickPayment(false);
      setQuickPaymentInvoice(null);
      fetchInvoices();
      alert('Payment recorded.');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Unable to record payment.');
    } finally {
      setQuickPaymentSaving(false);
    }
  };

  const openEstimatePicker = async () => {
    try {
      const response = await axios.get(`${API}/sales/estimates`);
      setEstimateOptions(response.data);
      setEstimateSearch('');
      setShowEstimatePicker(true);
    } catch (error) {
      console.error('Error loading estimates:', error);
      alert('Unable to load estimates.');
    }
  };

  const handleCreateFromEstimate = async (estimateId) => {
    try {
      const response = await axios.post(`${API}/sales/estimates/${estimateId}/invoice`);
      setShowEstimatePicker(false);
      if (onEdit) {
        onEdit(response.data);
      }
    } catch (error) {
      console.error('Error creating invoice from estimate:', error);
      alert('Unable to create invoice from this estimate.');
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch =
      invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toString().includes(searchTerm);

    let matchesStatus = false;
    const balance = Number(invoice.balance_due ?? 0);
    const paid = Number(invoice.amount_paid ?? 0);
    const total = Number(invoice.total ?? 0);
    const isFullyPaid = (total > 0 && balance <= 0.01) || invoice.status === 'PAID';
    const isPartial = !isFullyPaid && paid > 0;

    if (statusFilter === 'UNPAID') {
      matchesStatus = !isFullyPaid && !isPartial;
    } else if (statusFilter === 'PARTIAL') {
      matchesStatus = isPartial;
    } else if (statusFilter === 'PAID') {
      matchesStatus = isFullyPaid;
    }

    return matchesSearch && matchesStatus;
  });

  const groupedInvoices = filteredInvoices.reduce((groups, invoice) => {
    const date = new Date(invoice.date || invoice.created_date);
    const monthYear = date.toLocaleString('en-CA', { month: 'long', year: 'numeric' });
    if (!groups[monthYear]) groups[monthYear] = [];
    groups[monthYear].push(invoice);
    return groups;
  }, {});

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-700';
      case 'PARTIAL':
        return 'bg-yellow-100 text-yellow-700';
      case 'OVERDUE':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const handlePreviewInvoice = (invoice) => {
    setPreviewInvoice({ ...invoice, preview_stamp: Date.now() });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mb-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                data-testid="sales-invoices-export"
              >
                Export
              </button>
              <button
                onClick={openEstimatePicker}
                className="w-full sm:w-auto px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg font-medium hover:bg-indigo-50"
                data-testid="sales-invoices-from-estimate"
              >
                From Estimate
              </button>
              <button
                onClick={onCreate}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-semibold"
                data-testid="sales-invoices-new"
              >
                New Invoice
              </button>
            </div>
          </div>

          <input
            type="text"
            placeholder="Search invoices by Name, Address, or Invoice #"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 mb-4"
            data-testid="sales-invoices-search"
          />
          {loadError && (
            <div className="text-xs text-red-600 mb-3" data-testid="sales-invoices-load-error">
              {loadError}
            </div>
          )}

          <div className="flex flex-wrap gap-3 sm:gap-6 border-b">
            {['UNPAID', 'PARTIAL', 'PAID'].map(tab => {
              const count = invoices.filter(inv => {
                const bal = Number(inv.balance_due ?? 0);
                const pd = Number(inv.amount_paid ?? 0);
                const tot = Number(inv.total ?? 0);
                const fp = (tot > 0 && bal <= 0.01) || inv.status === 'PAID';
                const pp = !fp && pd > 0;
                if (tab === 'UNPAID') return !fp && !pp;
                if (tab === 'PARTIAL') return pp;
                return fp;
              }).length;
              return (
                <button
                  key={tab}
                  onClick={() => setStatusFilter(tab)}
                  className={`pb-3 px-2 text-sm sm:text-base font-semibold transition-colors relative ${
                    statusFilter === tab
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  data-testid={`sales-invoices-tab-${tab.toLowerCase()}`}
                >
                  {tab} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Loading invoices...</div>
          </div>
        ) : Object.keys(groupedInvoices).length === 0 ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600" data-testid="sales-invoices-empty">No invoices found</div>
            <button
              onClick={onCreate}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold"
              data-testid="sales-invoices-create-first"
            >
              Create Your First Invoice
            </button>
          </div>
        ) : (
          Object.entries(groupedInvoices).map(([monthYear, monthInvoices]) => {
            const monthTotal = monthInvoices.reduce((sum, inv) => sum + inv.total, 0);
            return (
              <div key={monthYear} className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800" data-testid={`sales-invoices-month-${monthYear.replace(/\s/g, '-')}`}>
                    {monthYear}
                  </h2>
                  <div className="text-base sm:text-lg font-semibold text-gray-600" data-testid={`sales-invoices-month-total-${monthYear.replace(/\s/g, '-')}`}>
                    Total: ${formatCurrency(monthTotal)}
                  </div>
                </div>
                <div className="space-y-3">
                  {monthInvoices.map(invoice => {
                    const rawBalance = invoice.balance_due !== undefined
                      ? Number(invoice.balance_due)
                      : (Number(invoice.total || 0) - Number(invoice.amount_paid || 0));
                    const displayBalance = Math.max(0, Math.round(rawBalance * 100) / 100);
                    const isPaid = displayBalance <= 0.01;
                    const statusLabel = isPaid ? 'paid' : (invoice.status || '');
                    return (
                      <div
                        key={invoice.id}
                        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow cursor-pointer"
                        data-testid={`sales-invoice-card-${invoice.id}`}
                        onClick={() => onEdit(invoice)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onEdit(invoice);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900" data-testid={`sales-invoice-client-${invoice.id}`}>
                              {invoice.client_name} - #{invoice.invoice_number}
                            </h3>
                            {statusLabel && (
                              <span className={`px-2.5 py-0.5 rounded text-xs font-semibold ${getStatusBadge(statusLabel)}`} data-testid={`sales-invoice-status-${invoice.id}`}>
                                {statusLabel.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-3" data-testid={`sales-invoice-date-${invoice.id}`}>
                            {new Date(invoice.date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          <div className="text-sm text-gray-700 space-y-0.5" data-testid={`sales-invoice-contact-${invoice.id}`}>
                            <div>{invoice.client_address}</div>
                            <div>{invoice.client_city}{invoice.client_province ? `, ${invoice.client_province}` : ''} {invoice.client_postal_code}</div>
                            <div>{invoice.client_phone}</div>
                          </div>
                          <div className="mt-3" data-testid={`sales-invoice-opened-${invoice.id}`}>
                            <div className={`flex items-center gap-1.5 text-sm ${invoice.email_opened_count > 0 ? 'text-emerald-700' : 'text-gray-500'}`} data-testid={`sales-invoice-opened-status-${invoice.id}`}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                              </svg>
                              {invoice.email_opened_count > 0 ? `Opened ${invoice.email_opened_count}x` : 'Not opened yet'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-testid={`sales-invoice-last-opened-${invoice.id}`}>
                              Last opened: {invoice.last_opened_at ? new Date(invoice.last_opened_at).toLocaleString('en-CA') : '—'}
                            </div>
                          </div>
                        </div>
                        <div className="w-full lg:w-auto text-left lg:text-right flex flex-col items-start lg:items-end">
                          <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1" data-testid={`sales-invoice-total-${invoice.id}`}>
                            ${formatCurrency(invoice.total)}
                          </div>
                          {isPaid ? (
                            <div className="text-sm text-emerald-700 font-semibold mb-4" data-testid={`sales-invoice-paid-${invoice.id}`}>
                              Paid in full
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 mb-4" data-testid={`sales-invoice-balance-${invoice.id}`}>
                              {`Balance: $${formatCurrency(displayBalance)}`}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 w-full">
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewPickerId(p => p === invoice.id ? null : invoice.id);
                                }}
                                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-1.5 rounded-md font-medium text-sm flex items-center gap-1"
                                data-testid={`sales-invoice-preview-${invoice.id}`}
                              >
                                Preview
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {previewPickerId === invoice.id && (
                                <div className="absolute left-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPreviewPickerId(null); setPdfView({ type: 'invoice', id: invoice.id, title: `Invoice #${invoice.invoice_number}` }); }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                                    data-testid={`sales-invoice-view-as-invoice-${invoice.id}`}
                                  >
                                    <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    View as Invoice
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPreviewPickerId(null); setPdfView({ type: 'receipt', id: invoice.id, title: `Receipt #${invoice.invoice_number}` }); }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center gap-2"
                                    data-testid={`sales-invoice-view-as-receipt-${invoice.id}`}
                                  >
                                    <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    View as Receipt
                                  </button>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuickPayment(invoice);
                              }}
                              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                              data-testid={`sales-invoice-record-payment-${invoice.id}`}
                            >
                              Record Payment
                            </button>
                          </div>
                        </div>
                      </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showEstimatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="invoice-estimate-picker">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Create Invoice from Estimate</h3>
              <button
                onClick={() => setShowEstimatePicker(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="invoice-estimate-picker-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Search estimates..."
                value={estimateSearch}
                onChange={(e) => setEstimateSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-estimate-picker-search"
              />
              <div className="max-h-80 overflow-y-auto space-y-2">
                {estimateOptions
                  .filter(est =>
                    est.client_name?.toLowerCase().includes(estimateSearch.toLowerCase()) ||
                    est.estimate_number?.toString().includes(estimateSearch)
                  )
                  .map(est => (
                    <button
                      key={est.id}
                      onClick={() => handleCreateFromEstimate(est.id)}
                      className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-indigo-50"
                      data-testid={`invoice-estimate-picker-item-${est.id}`}
                    >
                      <div className="font-semibold text-gray-800">{est.client_name} · #{est.estimate_number}</div>
                      <div className="text-xs text-gray-500">${formatCurrency(est.total)} · {new Date(est.date).toLocaleDateString('en-CA')}</div>
                    </button>
                  ))}
                {estimateOptions.length === 0 && (
                  <div className="text-sm text-gray-500">No estimates available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {previewInvoice && (
        <HtmlPreviewModal
          title={`Invoice Preview #${previewInvoice.invoice_number || ''}`}
          url={`${API}/sales/invoices/${previewInvoice.id}/pdf?t=${previewInvoice.preview_stamp || Date.now()}`}
          onClose={() => setPreviewInvoice(null)}
          testId="invoice-preview-modal"
        />
      )}
      {pdfView && (
        <PdfViewerModal
          formType={pdfView.type}
          formId={pdfView.id}
          title={pdfView.title}
          onClose={() => setPdfView(null)}
        />
      )}
        {showQuickPayment && quickPaymentInvoice && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="invoice-quick-payment-modal">
            <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Record Payment</h3>
                <button
                  onClick={() => setShowQuickPayment(false)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="invoice-quick-payment-close"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={quickPaymentForm.amount}
                    onChange={(e) => setQuickPaymentForm({ ...quickPaymentForm, amount: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                    data-testid="invoice-quick-payment-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Payment Date</label>
                  <input
                    type="date"
                    value={quickPaymentForm.paid_date}
                    onChange={(e) => setQuickPaymentForm({ ...quickPaymentForm, paid_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                    data-testid="invoice-quick-payment-date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Method</label>
                  <input
                    type="text"
                    value={quickPaymentForm.method}
                    onChange={(e) => setQuickPaymentForm({ ...quickPaymentForm, method: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                    data-testid="invoice-quick-payment-method"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                  <textarea
                    value={quickPaymentForm.notes}
                    onChange={(e) => setQuickPaymentForm({ ...quickPaymentForm, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                    rows={3}
                    data-testid="invoice-quick-payment-notes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowQuickPayment(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                  data-testid="invoice-quick-payment-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => quickPaymentInvoice?.id && openAuthenticatedPdf(`${API}/sales/invoices/${quickPaymentInvoice.id}/receipt?t=${Date.now()}`)}
                  disabled={!quickPaymentInvoice?.id}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:text-gray-400"
                  data-testid="invoice-quick-payment-receipt"
                >
                  Receipt PDF
                </button>
                <button
                  onClick={handleQuickPaymentSave}
                  disabled={quickPaymentSaving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-400"
                  data-testid="invoice-quick-payment-save"
                >
                  {quickPaymentSaving ? 'Saving...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default InvoicesModule;
